import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import {
  HiOutlineUserGroup,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamation,
  HiOutlineTrendingUp,
} from 'react-icons/hi';
import { ReportGuide } from '../../components/SuperAdmin/ReportGuide';

const rateColor = (rate) =>
  rate >= 70 ? 'text-green-600' : rate >= 40 ? 'text-amber-500' : 'text-red-500';

const rateBg = (rate) =>
  rate >= 70 ? 'bg-green-500' : rate >= 40 ? 'bg-amber-500' : 'bg-red-500';

const RenewalAgentPerformance = () => {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['renewal-agent-performance'],
    queryFn: superAdminAPI.getRenewalAgentPerformance,
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const allAgents = Array.isArray(body.agents) ? body.agents : [];
  const totals = body.totals || {};
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, role: '', flag: '' });
  const agents = filterReportRows(allAgents, filters, {
    searchOf: (a) => [a.name, a.role],
  }).filter((a) =>
    (!filters.role || a.role === filters.role)
    && (!filters.flag || (filters.flag === 'pending' ? a.pending > 0 : a.overdue > 0)));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineUserGroup className="w-7 h-7 text-primary-500" />
            Renewal Team Performance
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            How each salesperson is doing with the renewal tasks given to them
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                     hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
        >
          <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <ReportGuide
        id="renewal-agent-performance"
        summary="Compare salespeople: how many renewal tasks they got, finished, and turned into renewals."
        points={[
          ["Assigned", "Renewal tasks given to the person."],
          ["Handled", "Tasks they finished."],
          ["Pending / Overdue", "Tasks not finished yet / past their due date."],
          ["Converted", "Tasks that ended with the customer renewing."],
          ["Conversion rate", "Converted ÷ assigned. Higher is better."],
          ["Avg days to finish", "How long they usually take to close a task."],
        ]}
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Overall Conversion', value: `${body.overallConversionRate ?? 0}%`, icon: HiOutlineTrendingUp, color: 'text-primary-500' },
          { label: 'Tasks Assigned', value: totals.assigned ?? 0, icon: HiOutlineUserGroup, color: 'text-blue-500' },
          { label: 'Completed', value: totals.completed ?? 0, icon: HiOutlineCheckCircle, color: 'text-green-500' },
          { label: 'Overdue', value: totals.overdue ?? 0, icon: HiOutlineExclamation, color: 'text-red-500' },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <Icon className={`w-5 h-5 ${t.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{t.value}</p>
                <p className="text-xs text-gray-500">{t.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Agents table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Agents</h2>
          <span className="text-xs text-gray-400">{agents.length} agent{agents.length !== 1 ? 's' : ''} · best conversion first</span>
        </div>

        {allAgents.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            showPriority={false}
            searchPlaceholder="Search salesperson…"
            extraActive={!!(filters.role || filters.flag)}
            onResetExtra={() => setFilters((f) => ({ ...f, role: '', flag: '' }))}
            exportProps={{
              fileName: 'renewal-agent-performance',
              getRows: () => agents,
              columns: [
                { header: 'Salesperson', value: (a) => a.name },
                { header: 'Role', value: (a) => a.role },
                { header: 'Assigned', value: (a) => a.assigned },
                { header: 'Handled', value: (a) => a.completed },
                { header: 'Pending', value: (a) => a.pending },
                { header: 'Overdue', value: (a) => a.overdue },
                { header: 'Converted', value: (a) => a.converted },
                { header: 'Conversion Rate %', value: (a) => a.conversionRate },
                { header: 'Avg Days to Complete', value: (a) => (a.completed > 0 ? a.avgDaysToComplete : '') },
              ],
            }}
          >
            <FilterSelect value={filters.role} onChange={(v) => setFilters((f) => ({ ...f, role: v }))}
              allLabel="All Roles" options={uniqueSorted(allAgents.map((a) => a.role)).map((r) => [r, r])} />
            <FilterSelect value={filters.flag} onChange={(v) => setFilters((f) => ({ ...f, flag: v }))}
              allLabel="All Agents" options={[['pending', 'Has pending renewals'], ['overdue', 'Has overdue renewals']]} />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading agent performance…</div>
        ) : agents.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allAgents.length > 0
              ? 'No agents match these filters'
              : 'No renewal tasks assigned yet — agent performance appears once renewals are handled'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Salesperson</th>
                  <th className="px-4 py-3">Assigned</th>
                  <th className="px-4 py-3">Handled</th>
                  <th className="px-4 py-3">Pending</th>
                  <th className="px-4 py-3">Overdue</th>
                  <th className="px-4 py-3">Converted</th>
                  <th className="px-4 py-3 min-w-[180px]">Conversion Rate</th>
                  <th className="px-4 py-3">Avg Days to Finish</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {agents.map((a) => (
                  <tr key={a._id || a.name} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-xs">
                            {(a.name || 'A').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{a.name}</p>
                          <p className="text-xs text-gray-400 capitalize">{a.role || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{a.assigned}</td>
                    <td className="px-4 py-3 text-gray-600">{a.completed}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        <HiOutlineClock className="w-3.5 h-3.5 text-amber-400" />
                        {a.pending}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        <HiOutlineExclamation className="w-3.5 h-3.5 text-red-400" />
                        {a.overdue}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{a.converted}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${rateColor(a.conversionRate)}`}>
                          {a.conversionRate}%
                        </span>
                        <div className="w-20 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${rateBg(a.conversionRate)}`}
                            style={{ width: `${Math.min(100, a.conversionRate)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {a.completed > 0 ? `${a.avgDaysToComplete} day${a.avgDaysToComplete !== 1 ? 's' : ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenewalAgentPerformance;